import type { CoinType } from '../cointype/CoinTypeStore';
import type { GameLabel } from '../game/types';
import type { BundleItem } from '../rewards/types';

export type PlayerLeaderboardStatus = 'pending' | 'started' | 'finished';
export type ScoreCalculationMethod = 'square_two' | 'standard';
export type TournamentType = 'bets' | 'wins';
export type TournamentStatus = 'scheduled' | 'upcoming' | 'ongoing' | 'finished';
export type TournamentGroupMode = 'regular' | 'teams';
export type PayTableMultiplierType = 'CATEGORY' | 'SLOT' | 'RANK';

export interface ThumbnailAsset {
  imageUrl: string;
  order?: number;
}

export interface LeaderboardPrize {
  packageId?: number;
  gcCoin: number;
  scCoin: number;
  bundleItems: BundleItem[];
}

export interface LeaderboardRanking {
  playerId: number;
  position: number;
  playerName: string;
  prize?: LeaderboardPrize;
  score: number;
}

export interface PrizePool {
  gcCoin: number;
  scCoin: number;
}

export interface Leaderboard {
  id?: string;
  rankings: LeaderboardRanking[];
  player: LeaderboardRanking;
  prizePool: number;
  fullPrizePool: PrizePool;
}

export interface PayTableMultiplier {
  id?: string;
  multiplierType: PayTableMultiplierType;
  coinType?: CoinType | null;
  targetId?: string | null;
  targetName?: string | null;
  maxPosition?: number | null;
  multiplierValue: number;
  isActive: boolean;
  thumbnailAssets: ThumbnailAsset[];
  createdAt?: Date;
  updatedAt?: Date;
}

export interface TournamentGroup {
  key: string;
  name: string;
  displayName?: string;
  description?: string;
  maxPositionDisplay: number;
  startDate: string;
  endDate: string;
  gameId?: string;
  gameName?: string;
  categoryId?: number;
  categoryName?: string;
  isActive: boolean;
  postMessageEndDate: string;
  preMessageStartDate: string;
  scoreCalculationMethod: ScoreCalculationMethod;
  type: TournamentType;
  mode: TournamentGroupMode;
  headerColorMainGc?: string;
  headerColorMainSc?: string;
  headerColorSecondaryGc?: string;
  headerColorSecondarySc?: string;
  headerImageUrl?: string;
  headerImageUrlSc?: string;
  widgetImageUrl?: string;
  payTableMultipliers: PayTableMultiplier[];
}

export interface PlayerTeam {
  teamId: string;
  name: string;
  thumbnailUrl: string;
}

export interface TeamLeaderboardData {
  teamId: string;
  name: string;
  thumbnailUrl: string;
  score: number;
  position: number;
  prizePool: number;
  isPlayerTeam: boolean;
  rankings: LeaderboardRanking[];
}

export interface TeamsLeaderboardsData {
  gc: TeamLeaderboardData[];
  sc: TeamLeaderboardData[];
}

export interface Tournament {
  key: string;
  name: string;
  tournamentGroupKey: string;
  tournamentGroup: TournamentGroup;
  status: TournamentStatus;
  playerStatus: PlayerLeaderboardStatus;
  leaderboards: {
    gc: Leaderboard;
    sc: Leaderboard;
  };
  teamsLeaderboards: TeamsLeaderboardsData;
  prizes: {
    gc: LeaderboardPrize[];
    sc: LeaderboardPrize[];
  };
  gameLabel: GameLabel | null;
  playerTeam?: PlayerTeam;
  headerColorMainGc?: string;
  headerColorMainSc?: string;
  headerColorSecondaryGc?: string;
  headerColorSecondarySc?: string;
}

export enum TournamentOfChampionsState {
  PRE_CHAMPIONS = 'PRE_CHAMPIONS',
  ACTIVE_ROUND = 'ACTIVE_ROUND',
  BETWEEN_ROUNDS_QUALIFIED = 'BETWEEN_ROUNDS_QUALIFIED',
  BETWEEN_ROUNDS_ELIMINATED = 'BETWEEN_ROUNDS_ELIMINATED',
  CHAMPIONS_COMPLETED = 'CHAMPIONS_COMPLETED',
}

export interface TournamentOfChampionsRound {
  roundIndex: number;
  title: string | null;
  cupActiveImg: string | null;
  cupInactiveImg: string | null;
  widgetDesignImg?: string | null;
  tournamentGroupKey: string;
  startDate: string | null;
  endDate: string | null;
  isFinalized: boolean;
  playerPosition: number | null;
  qualified: boolean | null;
  playersToAdvanceLimit: number | null;
  prizePool: PrizePool | null;
  playerPrize?: LeaderboardPrize;
  roundAcknowledged?: boolean;
  playerTournamentKey?: string | null;
}

export interface TournamentOfChampionsAssets {
  bannerBackgroundImg: string | null;
  bannerAnimation: string | null;
  summaryAnimation?: string | null;
  betweenRoundsWidgetImg: string | null;
  introAnimation?: string | null;
  endOfRoundAnimation?: string | null;
  betweenRoundsAnimation?: string | null;
  shareBackgroundImg: string | null;
  qualificationLineQualifiedImg: string | null;
  qualificationLineEliminatedImg: string | null;
  leaderboardTrophyAnimation: string | null;
}

export interface TournamentOfChampions {
  state: TournamentOfChampionsState;
  championsKey: string;
  currentRoundIndex: number;
  isEliminated: boolean;
  rounds: TournamentOfChampionsRound[];
  assets: TournamentOfChampionsAssets;
  payTableMultipliers?: PayTableMultiplier[];
}

export type TournamentEventShownBy = 'log_in' | 'click' | 'widget_click';

export type TournamentEventParams = {
  popup_type?: 'start_in' | 'opening' | 'leaderboard' | 'finished';
  button_text?: string;
  headline?: string;
  shown_by?: TournamentEventShownBy;
  gift_prize?: LeaderboardPrize;
};

export type TournamentResponse = {
  tournament: Tournament | null;
};

export type TournamentV2Response = {
  tournaments: {
    currentTournament: Tournament | null;
    previousTournament: Tournament | null;
    tournamentOfChampions: TournamentOfChampions | null;
  };
};
